import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API });

// Attach JWT bearer token from localStorage on every request
client.interceptors.request.use((config) => {
  const token = localStorage.getItem("sutra_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// ---- Public content ----
export const getServices = () => client.get("/services").then((r) => r.data);
export const getService = (slug) => client.get(`/services/${slug}`).then((r) => r.data);
export const getProjects = (category) =>
  client.get("/projects", { params: category ? { category } : {} }).then((r) => r.data);
export const getTestimonials = () => client.get("/testimonials").then((r) => r.data);
export const getBlogs = (params = {}) => client.get("/blogs", { params }).then((r) => r.data);
export const getBlog = (slug) => client.get(`/blogs/${slug}`).then((r) => r.data);
export const getSite = () => client.get("/site").then((r) => r.data);

export function getUTM() {
  const p = new URLSearchParams(window.location.search);
  return {
    utm_source: p.get("utm_source") || "",
    utm_medium: p.get("utm_medium") || "",
    utm_campaign: p.get("utm_campaign") || "",
  };
}

export const createLead = (payload) =>
  client.post("/leads", { ...getUTM(), ...payload }).then((r) => r.data);

export const otpSend = (phone) => client.post("/otp/send", { phone }).then((r) => r.data);
export const otpVerify = (phone, code) => client.post("/otp/verify", { phone, code }).then((r) => r.data);

// ---- Auth ----
export const apiSignup = (body) => client.post("/auth/signup", body).then((r) => r.data);
export const apiLogin = (body) => client.post("/auth/login", body).then((r) => r.data);
export const apiMe = () => client.get("/auth/me").then((r) => r.data);

// ---- Admin ----
export const adminLeads = () => client.get("/leads").then((r) => r.data);
export const adminUpdateLead = (id, body) => client.patch(`/leads/${id}`, body).then((r) => r.data);
export const leadsExportUrl = () =>
  `${API}/leads/export?token=${encodeURIComponent(localStorage.getItem("sutra_token") || "")}`;

export const updateSite = (body) => client.put("/site", body).then((r) => r.data);

export const adminCreateProject = (b) => client.post("/admin/projects", b).then((r) => r.data);
export const adminUpdateProject = (slug, b) => client.put(`/admin/projects/${slug}`, b).then((r) => r.data);
export const adminDeleteProject = (slug) => client.delete(`/admin/projects/${slug}`).then((r) => r.data);

export const adminCreateTestimonial = (b) => client.post("/admin/testimonials", b).then((r) => r.data);
export const adminUpdateTestimonial = (name, b) => client.put(`/admin/testimonials/${encodeURIComponent(name)}`, b).then((r) => r.data);
export const adminDeleteTestimonial = (name) => client.delete(`/admin/testimonials/${encodeURIComponent(name)}`).then((r) => r.data);

export const adminCreateBlog = (b) => client.post("/admin/blogs", b).then((r) => r.data);
export const adminUpdateBlog = (slug, b) => client.put(`/admin/blogs/${slug}`, b).then((r) => r.data);
export const adminDeleteBlog = (slug) => client.delete(`/admin/blogs/${slug}`).then((r) => r.data);

// ---- Client Projects (customer dashboard + admin management) ----
export const myProjects = () => client.get("/me/projects").then((r) => r.data);
export const adminListClientProjects = () => client.get("/admin/client-projects").then((r) => r.data);
export const adminCustomers = () => client.get("/admin/customers").then((r) => r.data);
export const adminCreateClientProject = (b) => client.post("/admin/client-projects", b).then((r) => r.data);
export const adminUpdateClientProject = (id, b) => client.put(`/admin/client-projects/${id}`, b).then((r) => r.data);
export const adminDeleteClientProject = (id) => client.delete(`/admin/client-projects/${id}`).then((r) => r.data);
export const adminAddUpdate = (id, b) => client.post(`/admin/client-projects/${id}/updates`, b).then((r) => r.data);
export const adminDeleteUpdate = (id, uid) => client.delete(`/admin/client-projects/${id}/updates/${uid}`).then((r) => r.data);
export const adminAddDocument = (id, b) => client.post(`/admin/client-projects/${id}/documents`, b).then((r) => r.data);
export const adminDeleteDocument = (id, did) => client.delete(`/admin/client-projects/${id}/documents/${did}`).then((r) => r.data);

// ---- File uploads (Emergent object storage via backend) ----
export const uploadFile = async (file, kind = "document", projectId = "") => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("kind", kind);
  if (projectId) fd.append("project_id", projectId);
  const r = await client.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return r.data;
};

// Build a downloadable URL for a stored file id (query-string auth so <img>/<a> work)
export const fileUrl = (idOrPath) => {
  const token = localStorage.getItem("sutra_token") || "";
  const path = idOrPath.startsWith("/api/files/") ? idOrPath : `/api/files/${idOrPath}`;
  return `${BACKEND_URL}${path}?auth=${encodeURIComponent(token)}`;
};
